// @ts-nocheck

import React, { useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import { __ } from '../../../../i18n/l10n';
import GridsBasketButtonContainer from '../../containers/basket/GridsBasketButtonContainer';
import GridsBasketGridContainer from '../../containers/basket/GridsBasketGridContainer';
import GridsBasketStakeSelectorContainer from '../../containers/basket/GridsBasketStakeSelectorContainer';
import GridsTogglePrivateContainer from '../../containers/basket/GridsTogglePrivateContainer';
import GridsTypeTabsContainer from '../../containers/GridsTypeTabsContainer';
import { GridTypes } from '../../types';
import type { GridType } from '../../types';
import PoolTitle from '../pool/PoolTitle';

import StakeLabel from './StakeLabel';

const MainBloc = styled.div`
  border-radius: 0.6em;
  margin: 0 0 1em;
  ${({ $isHide, theme }) =>
    !Target.__MOBILE__
      ? css`
          background: ${theme.grids.colors.black};
          padding: 1em;
          position: relative;
          z-index: 0;
          transition:
            transform 300ms ease-out,
            opacity 300ms ease-out;
          pointer-events: ${$isHide ? 'none' : 'auto'};
          transform: ${$isHide ? 'translate3d(-100%, 0, 0)' : 'none'};
          opacity: ${$isHide ? 0 : 1};
        `
      : css`
          padding: 0;
        `}
`;

const StyledPoolTitle = styled(PoolTitle)`
  ${({ theme }) => css`
    color: ${theme.grids.colors.black};
    letter-spacing: 0.2em;
    margin-top: 0;

    .grid-picto {
      stroke: ${theme.grids.colors.black};
    }
  `}
`;

const Info = styled.p`
  font-family: BettingPoliticaBold, sans-serif;
  color: ${({ theme }) => (Target.__MOBILE__ ? theme.grids.colors.darkgrey4 : theme.grids.colors.middlegrey2)};
  margin: 0.6em 0;
`;

const BoldInfo = styled(Info)`
  font-family: BettingPoliticaExtraBold, sans-serif;
`;

const StyledGridsTogglePrivateContainer = styled(GridsTogglePrivateContainer)`
  margin: 1em 0;
`;

const StyledGridsBasketStakeSelectorContainer = styled(GridsBasketStakeSelectorContainer)``;

const StyledGridsBasketButtonContainer = styled(GridsBasketButtonContainer)`
  padding: 1em 0 0;
`;

const MinHeight = styled.div`
  min-height: 200px;
  color: ${({ theme }) => theme.grids.colors.middlegrey2};
`;

const renderInfo = () => (
  <Info>
    {__('Your grid will only be validated once it has been entirely financed.')}{' '}
    {__("Eventual winnings will be shared in proportion to each player's stake.")}
  </Info>
);

type GridsBasketContentProps = {
  className?: string;
  amount: number;
  amountLabel: string;
  gridType: GridType;
  poolNumber: number;
  gridReference: string;
  isFunding: boolean;
  shouldHideBasket: boolean;
};

const GridsBasketContent = ({
  className,
  amount,
  amountLabel,
  gridType,
  poolNumber,
  gridReference,
  isFunding,
  shouldHideBasket, // on desktop public basket is hide until click on "take part" cta
}: GridsBasketContentProps) => {
  const isTeamGrid = useMemo(() => gridType === GridTypes.TEAM, [gridType]);

  const renderMobile = useCallback(
    () => (
      <MainBloc className={className}>
        {!isFunding && <StyledPoolTitle poolNumber={poolNumber} withPicto />}
        {!isFunding && <GridsTypeTabsContainer isDark={!Target.__MOBILE__} />}
        <GridsBasketGridContainer gridReference={gridReference} />
        {!isTeamGrid && <StakeLabel stake={amount} label={amountLabel} />}
        {isTeamGrid && (
          <>
            {!isFunding && <StyledGridsTogglePrivateContainer isDark />}
            <StakeLabel stake={amount} label={amountLabel} />
            <StyledGridsBasketStakeSelectorContainer />
            {renderInfo()}
          </>
        )}
      </MainBloc>
    ),
    [amount, amountLabel, className, gridReference, isFunding, isTeamGrid, poolNumber],
  );

  const renderDesktop = useCallback(
    () => (
      <MainBloc className={className} $isHide={shouldHideBasket}>
        {!isFunding && <GridsTypeTabsContainer isDark />}
        <MinHeight>
          {!isTeamGrid && (
            <>
              <BoldInfo>{__('Tick the boxes in your grid.')}</BoldInfo>{' '}
              <Info>{__('You can select doubles and triples to increase your chances of winning.')}</Info>
            </>
          )}
          {isTeamGrid && (
            <>
              {!isFunding && <StyledGridsTogglePrivateContainer />}
              <>
                <StyledGridsBasketStakeSelectorContainer />
                {renderInfo()}
              </>
            </>
          )}
        </MinHeight>
        <StakeLabel isDesktopStyle stake={amount} label={amountLabel} />
        <StyledGridsBasketButtonContainer />
      </MainBloc>
    ),
    [amount, amountLabel, className, isFunding, isTeamGrid, shouldHideBasket],
  );

  return Target.__MOBILE__ ? renderMobile() : renderDesktop();
};

export default GridsBasketContent;
